import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  HrVenturePointsGetByEmployeeSettingDocument,
  HrVenturePointsCreateDocument,
  HrVenturePointsDeleteDocument,
  HrVenturePointsListPaginatedDocument,
  HrVenturePointsUpdateDocument,
  HrVenturePointsDeleteByEmployeeSettingDocument,
  VentureProjectProfileListPortfolioDealByProductFundDocument,
} from "api/generated";
import { ProjectProfileListByProductFundResponse } from "api";
import { Optional } from "config";

import {
  IHrVenturePointsPaginatedResponse,
  IHrVenturePointsUpdateVars,
  IHrVenturePointsCreateVars,
  IHrVenturePointsPaginatedVars,
  IHrVenturePointsResponse,
} from "./types";

export async function getHrVenturePointsListPaginated(
  input: IHrVenturePointsPaginatedVars
): Promise<Optional<IHrVenturePointsPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrVenturePointsListPaginatedDocument,
      variables: input,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrVenturePointsListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getProjectProfilesPortfolioDealByProductFund(
  productFundId: number[]
): Promise<ProjectProfileListByProductFundResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureProjectProfileListPortfolioDealByProductFundDocument,
      variables: {
        productFundId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureProjectProfileListPortfolioDealByProductFund;
  } catch (error) {
    throw error;
  }
}

export async function getHrVenturePointsById(
  employeeId: number,
  settingsId: number
): Promise<IHrVenturePointsResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrVenturePointsGetByEmployeeSettingDocument,
      variables: {
        employeeId,
        settingsId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrVenturePointsGetByEmployeeSetting ?? {};
  } catch (error) {
    throw error;
  }
}

export async function createHrVenturePoints(
  input: IHrVenturePointsCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrVenturePointsCreateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "HrVenturePoints",
      fieldNames: ["hrVenturePointsListPaginated"],
    });

    return res?.data?.hrVenturePointsCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateHrVenturePoints(
  input: IHrVenturePointsUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrVenturePointsUpdateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "HrVenturePoints",
      id: input.id?.toString(),
      fieldNames: ["hrVenturePointsListPaginated", "hrVenturePointsGetById"],
    });

    return res?.data?.hrVenturePointsUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteHrVenturePoints(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrVenturePointsDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "HrVenturePoints",
      id: id.toString(),
      fieldNames: ["hrVenturePointsListPaginated", "hrVenturePointsGetById"],
    });

    return res?.data?.hrVenturePointsDelete ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteByEmployeeSettingHrVenturePoints(
  employeeId: number,
  settingsId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrVenturePointsDeleteByEmployeeSettingDocument,
      variables: {
        employeeId,
        settingsId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "HrVenturePoints",
      fieldNames: ["hrVenturePointsListPaginated", "hrVenturePointsGetById"],
    });

    return res?.data?.hrVenturePointsDeleteByEmployeeSetting ?? false;
  } catch (error) {
    throw error;
  }
}
